'use strict';

/**
 * thermalPrinter.js
 *
 * Bridge para impresoras térmicas BLE (58mm ESC/POS genéricas).
 *
 * Flujo:
 *   1. thermalPrinter.init()         — solicita permisos BLE
 *   2. thermalPrinter.scan(cb)       — escanea / abre picker de dispositivos
 *   3. thermalPrinter.connect(id)    — conecta y detecta characteristic de escritura
 *   4. thermalPrinter.printReceipt() — genera bytes ESC/POS y los envía
 *   5. thermalPrinter.disconnect()   — cierra la conexión
 *
 * En la app de Android, Bluetooth clásico y USB van por el plugin propio
 * (plugins/printer): es como hablan casi todas las térmicas de 58 y 80 mm.
 * En app nativa queda además Bluetooth de bajo consumo con
 * @capacitor-community/bluetooth-le (la única opción en iOS).
 * En PWA/navegador usa Web Bluetooth y WebUSB (Chrome).
 */

import { BleClient } from '@capacitor-community/bluetooth-le';
import { Capacitor } from '@capacitor/core';
import { GcPrinter } from '@geekcommerce/printer';
import { planFactura } from '../lib/plan.js';
import { agruparPorComensal } from '../lib/comensales.js';

const isNative  = Capacitor.isNativePlatform();
const hasWebBT  = !isNative && 'bluetooth' in navigator;
const hasWebUSB = !isNative && 'usb' in navigator;
const isAndroid = isNative && Capacitor.getPlatform() === 'android';

// ─── Estado interno ───────────────────────────────────────────────────────────

// Android: Bluetooth clásico ('bt') o USB ('usb-android') por el plugin propio.
// { transporte, deviceId } de la conexión abierta, para reabrirla si se cae.
let _android = null;

// Capacitor BLE
let _deviceId   = null;
let _serviceId  = null;
let _charId     = null;

// Web Bluetooth
let _webDevice  = null;
let _webChar    = null;
// Almacena BluetoothDevice por id para que connect(id) pueda recuperarlo
const _webDeviceMap = new Map();

// WebUSB
let _usbDevice   = null;
let _usbIface    = null;
let _usbEndpoint = null;

// Clase USB de impresora — la preferimos al buscar el endpoint de escritura
const USB_CLASS_PRINTER = 0x07;

/**
 * Los USBDevice no exponen un id estable como los BluetoothDevice.
 * Componemos uno con vendor/product/serial para poder re-encontrarlo
 * en navigator.usb.getDevices() tras recargar.
 */
const usbKey = (d) =>
  `usb:${d.vendorId}:${d.productId}:${d.serialNumber ?? ''}`;

// UUIDs de servicios comunes en impresoras ESC/POS Bluetooth
const PRINTER_SERVICES = [
  '000018f0-0000-1000-8000-00805f9b34fb', // ESC/POS genérico
  '49535343-fe7d-4ae5-8fa9-9fafd205e455', // ISSC Transparent
  '0000ff00-0000-1000-8000-00805f9b34fb', // Serial genérico FF00
  '0000ffe0-0000-1000-8000-00805f9b34fb', // HM-10 / JDY
  '6e400001-b5a3-f393-e0a9-e50e24dcca9e', // Nordic UART Service
  'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // Xprinter
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const strip = (str = '') =>
  str.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase();

const encoder = new TextEncoder();

// Genera el bloque de bytes ESC/POS para el recibo
function buildReceipt({ business, date, cashier, customer, folio, items = [], subtotal, discount, total, tip = 0, method, changeDue, note, porComensal = null, comensales = [], factura = planFactura() }) {
  const chunks = [];

  const push = (str) => chunks.push(encoder.encode(str));
  const cmd  = (...bytes) => chunks.push(new Uint8Array(bytes));

  const LINE  = '-'.repeat(32) + '\n';
  const DLINE = '='.repeat(32) + '\n';

  const padLine = (left, right) => {
    const gap = 32 - left.length - right.length;
    return left + ' '.repeat(Math.max(gap, 1)) + right + '\n';
  };

  const METHOD_LABEL = { cash: 'Efectivo', card: 'Tarjeta', transfer: 'Transferencia', mixed: 'Mixto' };
  const fmt = (n) => `$${Number(n).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

  // ESC @ — initialize
  cmd(0x1B, 0x40);

  // ── Header centrado ──
  cmd(0x1B, 0x61, 0x01); // center
  cmd(0x1D, 0x21, 0x11); // double size
  push(strip(business?.name ?? 'GEEK COMMERCE') + '\n');
  cmd(0x1D, 0x21, 0x00); // normal size
  if (business?.rfc)     push('RFC: ' + business.rfc + '\n');
  if (business?.address) push(strip(business.address) + '\n');
  if (business?.phone)   push('Tel: ' + business.phone + '\n');

  // ── Metadata ──
  cmd(0x1B, 0x61, 0x00); // left
  push(LINE);
  push(date + '\n');
  if (cashier)  push('Cajero:  ' + strip(cashier)  + '\n');
  if (customer) push('Cliente: ' + strip(customer) + '\n');
  push('Folio: ' + folio + '\n');
  push(LINE);

  // ── Ítems ──
  //
  // Agrupados por comensal con la misma regla que la comanda de cocina: lo
  // compartido sale UNA vez en su propio bloque, no repetido con cada quien.
  // Sin comensales capturados es un solo bloque sin encabezado, o sea el
  // ticket de siempre — un mostrador no debe empezar a ver títulos.
  for (const grupo of agruparPorComensal(items, comensales)) {
    if (grupo.etiqueta) {
      cmd(0x1B, 0x45, 0x01);
      push(strip(grupo.etiqueta) + '\n');
      cmd(0x1B, 0x45, 0x00);
    }

    for (const item of grupo.items) {
      const name        = strip(item.name).slice(0, 22);
      const lineTotal   = fmt(item.quantity * item.unit_price);
      const origPrice   = item.original_price ?? null;
      const hasDiscount = origPrice && origPrice > item.unit_price;

      if (item.quantity > 1) {
        push(`  ${item.quantity}x ${strip(item.name)}\n`);
        if (hasDiscount) push(padLine('  Precio lista', fmt(origPrice)));
        push(padLine('', lineTotal));
      } else {
        push(padLine('  ' + name, lineTotal));
        if (hasDiscount) push(padLine('  Precio lista:', fmt(origPrice)));
      }

      // Modificadores debajo del platillo, indentados
      for (const m of item.modifiers ?? []) {
        const etiqueta = '    - ' + strip(m.option_name).slice(0, 20);
        if (m.price_delta) push(padLine(etiqueta, fmt(m.price_delta)));
        else               push(etiqueta + '\n');
      }
    }
  }

  push(LINE);

  // ── Totales ──
  if (discount > 0) {
    push(padLine('Subtotal', fmt(subtotal)));
    push(padLine('Descuento', '-' + fmt(discount)));
  }
  if (tip > 0) {
    // Con propina hay que desglosar para que el cliente vea qué es cada cosa
    if (discount === 0) push(padLine('Mercancia', fmt(total)));
    push(padLine('Propina', fmt(tip)));
  }
  if (discount > 0 || tip > 0) push(DLINE);

  cmd(0x1D, 0x21, 0x01); // double height for total
  push(padLine('TOTAL', fmt(total + tip)));
  cmd(0x1D, 0x21, 0x00);

  push(padLine(METHOD_LABEL[method] ?? method, ''));
  if (method === 'cash' && changeDue > 0) {
    push(padLine('Cambio', fmt(changeDue)));
  }

  // ── Le toca a cada uno ──
  //
  // La cuenta se cobró completa a quien la pagó; esto es para que en la mesa se
  // repartan sin sacar la calculadora. Las cifras vienen ya calculadas del
  // carrito —lo compartido prorrateado, el descuento en proporción a lo
  // consumido y el redondeo cuadrado— para que sumen EXACTAMENTE el total.
  if (porComensal?.length > 1) {
    push(LINE);
    cmd(0x1B, 0x45, 0x01);
    push('LE TOCA A CADA UNO\n');
    cmd(0x1B, 0x45, 0x00);
    for (const parte of porComensal) {
      push(padLine(strip(parte.nombre || 'Comensal ' + parte.seat), fmt(parte.total)));
      // Sin esto no cuadra a ojo: sus platos suman menos de lo que paga, y la
      // diferencia es la botella que compartió.
      if (parte.compartido > 0) {
        push('  incluye ' + fmt(parte.compartido) + ' compartido\n');
      }
    }
  }

  // ── Nota ──
  if (note) {
    push(LINE);
    push('Nota: ' + strip(note) + '\n');
  }

  // ── Footer ──
  cmd(0x1B, 0x61, 0x01); // center
  push('\n');
  push('Este comprobante no es CFDI.\n');
  // Sin facturación en el plan no se le promete al cliente una factura que
  // el negocio no puede emitir desde aquí.
  if (factura) {
    push('Solicita tu factura al\n');
    push('establecimiento.\n');
  }
  push('Gracias por su compra!\n');
  push('\n\n\n');

  // GS V 66 3 — partial cut con avance de 3 líneas
  cmd(0x1D, 0x56, 0x42, 0x03);

  // Concatenar todos los chunks en un solo Uint8Array
  const total_len = chunks.reduce((acc, c) => acc + c.byteLength, 0);
  const result    = new Uint8Array(total_len);
  let offset = 0;
  for (const c of chunks) {
    result.set(c, offset);
    offset += c.byteLength;
  }
  return result;
}

// Genera el bloque de bytes ESC/POS para el corte de caja
function buildCorte({ business, cashier, terminal, openedAt, closedAt, salesCount, salesTotal, openingCash, closingCash, expectedCash, breakdown, diff, notes }) {
  const chunks = [];
  const push   = (str) => chunks.push(encoder.encode(str));
  const cmd    = (...bytes) => chunks.push(new Uint8Array(bytes));

  const LINE  = '-'.repeat(32) + '\n';
  const DLINE = '='.repeat(32) + '\n';
  const padLine = (left, right) => {
    const gap = 32 - left.length - right.length;
    return left + ' '.repeat(Math.max(gap, 1)) + right + '\n';
  };
  const fmt = (n) => `$${Number(n ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

  cmd(0x1B, 0x40); // init
  cmd(0x1B, 0x61, 0x01); // center
  cmd(0x1D, 0x21, 0x11); // double size
  push(strip(business?.name ?? 'GEEK COMMERCE') + '\n');
  cmd(0x1D, 0x21, 0x00);
  if (business?.rfc) push('RFC: ' + business.rfc + '\n');
  push(LINE);
  cmd(0x1D, 0x21, 0x01);
  push('CORTE DE CAJA\n');
  cmd(0x1D, 0x21, 0x00);
  push('\n');

  cmd(0x1B, 0x61, 0x00); // left
  if (terminal)  push('Terminal: ' + strip(terminal) + '\n');
  if (cashier)   push('Cajero:   ' + strip(cashier)  + '\n');
  if (openedAt)  push('Apertura: ' + strip(openedAt) + '\n');
  if (closedAt)  push('Cierre:   ' + strip(closedAt) + '\n');
  push(LINE);

  push(padLine('Ventas', String(salesCount ?? 0)));
  cmd(0x1D, 0x21, 0x01);
  push(padLine('TOTAL VENDIDO', fmt(salesTotal)));
  cmd(0x1D, 0x21, 0x00);
  push(LINE);

  if (breakdown) {
    if ((breakdown.cash      ?? 0) > 0) push(padLine('  Efectivo',      fmt(breakdown.cash)));
    if ((breakdown.card      ?? 0) > 0) push(padLine('  Tarjeta',       fmt(breakdown.card)));
    if ((breakdown.transfer  ?? 0) > 0) push(padLine('  Transferencia', fmt(breakdown.transfer)));
    push(LINE);
  }

  // Propinas — van aparte del total vendido porque no son ingreso del negocio,
  // pero el efectivo sí está en el cajón y hay que repartirlo.
  if ((breakdown?.tips ?? 0) > 0) {
    cmd(0x1D, 0x21, 0x01);
    push(padLine('PROPINAS', fmt(breakdown.tips)));
    cmd(0x1D, 0x21, 0x00);
    push('(incluidas en el efectivo esperado)\n');
    push(LINE);
  }

  push(padLine('Efectivo inicial', fmt(openingCash)));
  push(padLine('Esperado en caja', fmt(expectedCash)));
  if (closingCash != null) {
    push(padLine('Contado al cierre', fmt(closingCash)));
    push(DLINE);
    if (diff === 0)      push(padLine('Sin diferencia', ''));
    else if (diff > 0)   push(padLine('Sobrante', '+' + fmt(diff)));
    else                 push(padLine('FALTANTE', fmt(diff)));
  }

  if (notes) {
    push(LINE);
    push('Notas: ' + strip(notes) + '\n');
  }

  cmd(0x1B, 0x61, 0x01);
  push('\n\n\n');
  cmd(0x1D, 0x56, 0x42, 0x03); // partial cut

  const total_len = chunks.reduce((acc, c) => acc + c.byteLength, 0);
  const result    = new Uint8Array(total_len);
  let offset = 0;
  for (const c of chunks) { result.set(c, offset); offset += c.byteLength; }
  return result;
}

// Genera el bloque ESC/POS de una comanda de cocina.
// Deliberadamente SIN precios: al cocinero no le sirven y solo estorban.
// Todo va en letra grande porque se lee de lejos, con prisa y con vapor.
function buildComanda({ folio, station, tabName, cashier, date, items = [], note, comensales = [] }) {
  const chunks = [];
  const push   = (str) => chunks.push(encoder.encode(str));
  const cmd    = (...bytes) => chunks.push(new Uint8Array(bytes));

  const LINE  = '-'.repeat(32) + '\n';
  const DLINE = '='.repeat(32) + '\n';

  cmd(0x1B, 0x40);        // init
  cmd(0x1B, 0x61, 0x01);  // centrado

  // ── Estación: lo primero que debe identificar quien la toma ──
  cmd(0x1D, 0x21, 0x11);  // doble alto y ancho
  push(strip(station || 'COCINA') + '\n');
  cmd(0x1D, 0x21, 0x00);

  // ── Mesa y folio, también grandes ──
  cmd(0x1D, 0x21, 0x11);
  if (tabName) push(strip(tabName) + '\n');
  push('#' + folio + '\n');
  cmd(0x1D, 0x21, 0x00);

  cmd(0x1B, 0x61, 0x00);  // izquierda
  push(LINE);
  push(date + '\n');
  if (cashier) push('Pidio: ' + strip(cashier) + '\n');
  push(DLINE);

  // ── Platillos ──
  //
  // Se agrupan por comensal cuando la mesa se capturó por persona. Va como
  // encabezado y no como marca en cada renglón porque en doble ancho caben 16
  // caracteres: un "[C3]" junto al nombre se lo comería. Y así se lee como
  // emplata la cocina — todo lo del puesto 3 junto— en vez de obligar al
  // cocinero a saltar entre renglones buscando de quién es cada cosa.
  const grupos = agruparPorComensal(items, comensales);

  for (const grupo of grupos) {
    if (grupo.etiqueta) {
      cmd(0x1B, 0x45, 0x01);         // negritas
      cmd(0x1B, 0x61, 0x01);         // centrado
      push(strip(grupo.etiqueta) + '\n');
      cmd(0x1B, 0x61, 0x00);
      cmd(0x1B, 0x45, 0x00);
    }

    for (const item of grupo.items) {
      cmd(0x1D, 0x21, 0x11);
      push(`${item.quantity}x ${strip(item.name).slice(0, 14)}\n`);
      cmd(0x1D, 0x21, 0x00);

      // Los modificadores son lo que más se equivoca la cocina: van en negritas
      for (const m of item.modifiers ?? []) {
        cmd(0x1B, 0x45, 0x01);
        push('   >> ' + strip(m.option_name) + '\n');
        cmd(0x1B, 0x45, 0x00);
      }
      if (item.note) {
        cmd(0x1B, 0x45, 0x01);
        push('   * ' + strip(item.note) + '\n');
        cmd(0x1B, 0x45, 0x00);
      }
      push('\n');
    }

    if (grupos.length > 1 && grupo !== grupos[grupos.length - 1]) push(LINE);
  }

  if (note) {
    push(DLINE);
    cmd(0x1B, 0x45, 0x01);
    push('NOTA: ' + strip(note) + '\n');
    cmd(0x1B, 0x45, 0x00);
  }

  push('\n\n\n');
  cmd(0x1D, 0x56, 0x42, 0x03); // corte parcial

  const total_len = chunks.reduce((acc, c) => acc + c.byteLength, 0);
  const result    = new Uint8Array(total_len);
  let offset = 0;
  for (const c of chunks) { result.set(c, offset); offset += c.byteLength; }
  return result;
}

// El puente de Capacitor solo pasa texto: los bytes ESC/POS viajan en base64.
function aBase64(bytes) {
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  }
  return btoa(bin);
}

// deviceId de una USB en Android: 'usb:<vendorId>:<productId>' (decimal). El
// nombre del puerto (/dev/bus/usb/…) cambia al reconectar; esto no.
const usbAndroidId = (d) => `usb:${d.vendorId}:${d.productId}`;
function usbAndroidDeId(deviceId) {
  const [, vendorId, productId] = String(deviceId).split(':');
  return { vendorId: Number(vendorId), productId: Number(productId) };
}

async function abrirAndroid(transporte, deviceId, { silencioso = false } = {}) {
  const r = transporte === 'bt'
    ? await GcPrinter.conectarBluetooth({ direccion: deviceId })
    : await GcPrinter.conectarUsb({ ...usbAndroidDeId(deviceId), pedirPermiso: !silencioso });
  _android = { transporte, deviceId };
  return r;
}

// BLE se prepara la primera vez que se usa, no al abrir la app. En Android
// initialize() pide "Dispositivos cercanos" con su propio diálogo, y si eso
// pasa mientras el plugin de impresoras pide el mismo permiso, Android
// rechaza el segundo sin preguntar: "Buscar impresoras" decía que no había
// permiso aunque se acababa de dar.
let _bleListo = null;
function bleListo() {
  if (!_bleListo) {
    _bleListo = BleClient.initialize({ androidNeverForLocation: true }).catch((err) => {
      _bleListo = null;
      throw err;
    });
  }
  return _bleListo;
}

// Divide el buffer en chunks para BLE (MTU típico 512 bytes)
async function writeChunked(deviceId, serviceId, charId, data) {
  const MTU    = 512;
  let   offset = 0;
  while (offset < data.byteLength) {
    const slice = data.slice(offset, offset + MTU);
    await BleClient.writeWithoutResponse(deviceId, serviceId, charId, new DataView(slice.buffer));
    offset += MTU;
    await new Promise((r) => setTimeout(r, 30));
  }
}

async function writeChunkedWeb(char, data) {
  const MTU    = 512;
  let   offset = 0;
  while (offset < data.byteLength) {
    const slice = data.slice(offset, offset + MTU);
    try {
      await char.writeValueWithoutResponse(slice);
    } catch {
      await char.writeValue(slice);
    }
    offset += MTU;
    await new Promise((r) => setTimeout(r, 30));
  }
}

// USB bulk soporta transferencias grandes, pero troceamos igual para
// no desbordar el buffer del firmware en impresoras baratas.
async function writeChunkedUsb(device, endpoint, data) {
  const CHUNK  = 4096;
  let   offset = 0;
  while (offset < data.byteLength) {
    await device.transferOut(endpoint, data.slice(offset, offset + CHUNK));
    offset += CHUNK;
  }
}

/**
 * Abre el USBDevice y reclama la interfaz con un endpoint bulk OUT.
 * Prefiere la interfaz de clase impresora (0x07); si no existe, toma
 * la primera que tenga un bulk OUT — muchas térmicas se presentan como
 * vendor-specific (0xFF).
 */
async function usbOpen(device) {
  await device.open();
  if (!device.configuration) await device.selectConfiguration(1);

  const candidates = [];
  for (const iface of device.configuration.interfaces) {
    for (const alt of iface.alternates) {
      const ep = alt.endpoints.find((e) => e.direction === 'out' && e.type === 'bulk');
      if (ep) {
        candidates.push({
          ifaceNum:   iface.interfaceNumber,
          altSetting: alt.alternateSetting,
          endpoint:   ep.endpointNumber,
          isPrinter:  alt.interfaceClass === USB_CLASS_PRINTER,
        });
      }
    }
  }

  const pick = candidates.find((c) => c.isPrinter) ?? candidates[0];
  if (!pick) {
    await device.close().catch(() => {});
    throw new Error('La impresora no expone un endpoint USB de escritura');
  }

  await device.claimInterface(pick.ifaceNum);
  if (pick.altSetting !== 0) {
    await device.selectAlternateInterface(pick.ifaceNum, pick.altSetting);
  }

  _usbDevice   = device;
  _usbIface    = pick.ifaceNum;
  _usbEndpoint = pick.endpoint;
  return { deviceId: usbKey(device), endpoint: pick.endpoint };
}

// ─── API pública ──────────────────────────────────────────────────────────────

export const thermalPrinter = {

  async init() {
    // En iOS BLE es la única vía y se prepara de una vez; en Android, cuando
    // alguien busque o conecte una impresora BLE.
    if (isNative && !isAndroid) {
      await bleListo();
    }
    // Web Bluetooth no requiere init — el permiso se solicita en requestDevice()
  },

  async scan(onDevice, timeoutMs = 8000) {
    if (isNative) {
      await bleListo();
      await BleClient.requestLEScan({}, (result) => {
        if (result.device?.deviceId) onDevice(result.device);
      });
      setTimeout(() => BleClient.stopLEScan().catch(() => {}), timeoutMs);
      return;
    }

    if (!hasWebBT) return;

    // Web Bluetooth: abre el selector de dispositivos del navegador
    const device = await navigator.bluetooth.requestDevice({
      acceptAllDevices: true,
      optionalServices: PRINTER_SERVICES,
    });

    _webDeviceMap.set(device.id, device);
    onDevice({ deviceId: device.id, name: device.name ?? 'Impresora BT' });
  },

  /**
   * Android: impresoras USB conectadas y Bluetooth ya emparejadas. Bluetooth
   * no se busca aquí: las térmicas SPP se emparejan en los ajustes del
   * sistema (con su PIN, 0000 o 1234) y de ahí salen en esta lista.
   * Devuelve { dispositivos, errorBluetooth }: sin permiso o con el
   * Bluetooth apagado, lo de USB se muestra igual.
   */
  async listarAndroid() {
    const dispositivos = [];
    let errorBluetooth = null;
    try {
      const { dispositivos: usb } = await GcPrinter.listarUsb();
      for (const d of usb) {
        dispositivos.push({ deviceId: usbAndroidId(d), name: d.nombre, transport: 'usb-android', impresora: d.impresora });
      }
    } catch { /* sin USB anfitrión: nada que listar */ }
    try {
      const { dispositivos: bt } = await GcPrinter.listarBluetooth();
      for (const d of bt) {
        dispositivos.push({ deviceId: d.direccion, name: d.nombre, transport: 'bt', impresora: d.impresora });
      }
    } catch (err) {
      errorBluetooth = err?.message ?? 'No se pudo leer el Bluetooth';
    }
    // Las impresoras primero; audífonos, bocinas y demás al final.
    dispositivos.sort((a, b) => Number(b.impresora) - Number(a.impresora));
    return { dispositivos, errorBluetooth };
  },

  abrirAjustesBluetooth() {
    return GcPrinter.abrirAjustesBluetooth();
  },

  /** Android: conecta una impresora de listarAndroid(). */
  async conectarAndroid(device) {
    await abrirAndroid(device.transport, device.deviceId);
  },

  async stopScan() {
    if (isNative && _bleListo) {
      await BleClient.stopLEScan().catch(() => {});
    }
    // Web Bluetooth: no hay escaneo continuo que detener
  },

  /** Abre el selector USB del navegador. Requiere gesto del usuario. */
  async scanUsb(onDevice) {
    if (!hasWebUSB) throw new Error('WebUSB no disponible en este navegador');

    const device = await navigator.usb.requestDevice({ filters: [] });
    onDevice({
      deviceId: usbKey(device),
      name: device.productName ?? `USB ${device.vendorId.toString(16)}`,
      transport: 'usb',
    });
  },

  async connectUsb(deviceId) {
    if (!hasWebUSB) throw new Error('WebUSB no disponible en este navegador');

    const devices = await navigator.usb.getDevices();
    const device  = devices.find((d) => usbKey(d) === deviceId);
    if (!device) throw new Error('Dispositivo USB no encontrado — conéctalo de nuevo');

    return usbOpen(device);
  },

  /**
   * Reconecta en silencio tras recargar la app, sin volver a pedir el
   * selector. Devuelve el transporte restaurado, o null si no se pudo.
   *
   * USB conserva el permiso entre recargas (navigator.usb.getDevices()).
   * Web Bluetooth solo lo conserva si el navegador expone getDevices(),
   * que en Chrome sigue detrás de un flag — por eso puede devolver null.
   */
  async autoReconnect(savedDeviceId, transport) {
    if (!savedDeviceId) return null;

    if (isNative) {
      try {
        if (isAndroid && (transport === 'bt' || transport === 'usb-android')) {
          await abrirAndroid(transport, savedDeviceId, { silencioso: true });
          return transport;
        }
        if (transport === 'native') {
          await this.connect(savedDeviceId);
          return 'native';
        }
      } catch {
        return null;
      }
      return null;
    }

    if (transport === 'usb' && hasWebUSB) {
      try {
        const devices = await navigator.usb.getDevices();
        const device  = devices.find((d) => usbKey(d) === savedDeviceId);
        if (!device) return null;
        await usbOpen(device);
        return 'usb';
      } catch {
        return null;
      }
    }

    if (transport === 'ble' && hasWebBT && navigator.bluetooth.getDevices) {
      try {
        const devices = await navigator.bluetooth.getDevices();
        const device  = devices.find((d) => d.id === savedDeviceId);
        if (!device) return null;
        _webDeviceMap.set(device.id, device);
        await this.connect(device.id);
        return 'ble';
      } catch {
        return null;
      }
    }

    return null;
  },

  async connect(deviceId) {
    if (isNative) {
      await bleListo();
      await BleClient.connect(deviceId, () => {
        _deviceId = _serviceId = _charId = null;
      });

      const services = await BleClient.getServices(deviceId);
      for (const svc of services) {
        for (const ch of svc.characteristics) {
          if (ch.properties.writeWithoutResponse || ch.properties.write) {
            _deviceId  = deviceId;
            _serviceId = svc.uuid;
            _charId    = ch.uuid;
            return { serviceId: svc.uuid, charId: ch.uuid };
          }
        }
      }

      await BleClient.disconnect(deviceId).catch(() => {});
      throw new Error('No se encontró un characteristic de escritura en la impresora');
    }

    // Web Bluetooth
    const device = _webDeviceMap.get(deviceId);
    if (!device) throw new Error('Dispositivo no encontrado — escanea de nuevo');

    device.addEventListener('gattserverdisconnected', () => {
      _webDevice = null;
      _webChar   = null;
    });

    const server = await device.gatt.connect();

    // Muchas impresoras BLE baratas no responden a getPrimaryServices() sin UUID.
    // Primero probamos cada UUID conocido individualmente; si ninguno funciona,
    // intentamos el listado genérico como fallback.
    let foundChar = null;

    for (const serviceUuid of PRINTER_SERVICES) {
      try {
        const svc   = await server.getPrimaryService(serviceUuid);
        const chars = await svc.getCharacteristics();
        for (const ch of chars) {
          if (ch.properties.writeWithoutResponse || ch.properties.write) {
            foundChar = ch;
            break;
          }
        }
        if (foundChar) break;
      } catch {
        // Este UUID no existe en esta impresora — continuar
      }
    }

    if (!foundChar) {
      try {
        const services = await server.getPrimaryServices();
        for (const svc of services) {
          const chars = await svc.getCharacteristics();
          for (const ch of chars) {
            if (ch.properties.writeWithoutResponse || ch.properties.write) {
              foundChar = ch;
              break;
            }
          }
          if (foundChar) break;
        }
      } catch {}
    }

    if (!foundChar) {
      device.gatt.disconnect().catch(() => {});
      throw new Error('No se encontró un characteristic de escritura en la impresora');
    }

    _webDevice = device;
    _webChar   = foundChar;
    return { serviceId: foundChar.service.uuid, charId: foundChar.uuid };
  },

  async disconnect() {
    if (_android) {
      _android = null;
      await GcPrinter.desconectar().catch(() => {});
    }
    if (isNative) {
      if (!_deviceId) return;
      await BleClient.disconnect(_deviceId).catch(() => {});
      _deviceId = _serviceId = _charId = null;
      return;
    }

    if (_webDevice?.gatt?.connected) {
      _webDevice.gatt.disconnect();
    }
    _webDevice = null;
    _webChar   = null;

    if (_usbDevice) {
      try {
        if (_usbIface != null) await _usbDevice.releaseInterface(_usbIface);
        await _usbDevice.close();
      } catch {}
    }
    _usbDevice = _usbIface = _usbEndpoint = null;
  },

  isConnected() {
    if (isNative) return !!_android || !!_deviceId;
    if (_usbDevice?.opened) return true;
    return _webDevice?.gatt?.connected ?? false;
  },

  /** Transporte activo: 'native' | 'usb' | 'ble' | null */
  activeTransport() {
    if (isNative) return _android?.transporte ?? (_deviceId ? 'native' : null);
    if (_usbDevice?.opened) return 'usb';
    if (_webDevice?.gatt?.connected) return 'ble';
    return null;
  },

  capabilities() {
    return { isNative, isAndroid, hasWebBT, hasWebUSB };
  },

  async printReceipt(data) {
    await this._send(buildReceipt(data));
  },

  async printCorte(data) {
    await this._send(buildCorte(data));
  },

  /**
   * Imprime una comanda de cocina.
   *
   * A diferencia del ticket, aquí NO se cae a window.print(): un diálogo de
   * impresión del sistema en medio del servicio interrumpe al mesero y la
   * comanda se pierde. Si no hay impresora, se avisa y la pantalla de cocina
   * queda como respaldo — la comanda ya está en el servidor.
   */
  async printComanda(data) {
    if (!this.isConnected()) throw new Error('Impresora no conectada');
    await this._send(buildComanda(data));
  },

  /** Enruta los bytes ESC/POS por el transporte activo. */
  async _send(bytes) {
    if (_android) {
      const datos = aBase64(bytes);
      try {
        await GcPrinter.escribir({ datos });
      } catch {
        // Se apagó, se alejó o se desconectó el cable: un intento de
        // reabrir antes de dar el error.
        const { transporte, deviceId } = _android;
        _android = null;
        await abrirAndroid(transporte, deviceId, { silencioso: true });
        await GcPrinter.escribir({ datos });
      }
      return;
    }

    if (isNative) {
      if (!_deviceId || !_serviceId || !_charId) throw new Error('Impresora no conectada');
      await writeChunked(_deviceId, _serviceId, _charId, bytes);
      return;
    }

    // USB primero: es más rápido y sobrevive recargas
    if (_usbDevice?.opened && _usbEndpoint != null) {
      await writeChunkedUsb(_usbDevice, _usbEndpoint, bytes);
      return;
    }

    if (_webChar && _webDevice?.gatt?.connected) {
      await writeChunkedWeb(_webChar, bytes);
      return;
    }

    // Sin impresora conectada — diálogo de impresión del sistema
    window.print();
  },
};
